import Twemoji from "react-twemoji";
import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";

import {
  AdminQueueTaskSummary,
  AdminQueueFilterType,
} from "sockapi/scope-specs/admin-queue-spec";
import { AdminQueueTaskType } from "sockapi/scope-specs/admin-queue-task-spec";
import { ScopeType } from "sockapi/scope-types";
import { useSockClient } from "stores/ServerInteractionStore";
import { useNavStore, useRedirectIfNonAdmin } from "stores/NavStore";
import { useAdminPreferencesStore } from "stores/UserPreferencesStore";
import {
  useGlobalScopeState,
  usePuzzleDisplayNames,
} from "stores/ScopeStateStore";
import { LargeSpinner } from "components/Loading";
import Modal from "components/Modal";
import { AdminQueueTaskDisplay } from "components/AdminQueue/AdminQueueTaskDisplay";
import AdminQueueTaskListDisplay from "components/AdminQueue/AdminQueueTaskListDisplay";
import { sendAdminQueueClaimStepAsync } from "components/AdminQueue/ClaimedBy";
import { clsObj } from "client-util/util";
import "./AdminQueuePage.css";

const ADMIN_QUEUE_TYPE_FILTERS: {
  type: AdminQueueFilterType;
  text: string;
  func: (taskSummary: AdminQueueTaskSummary) => boolean;
}[] = [
  {
    type: AdminQueueFilterType.HINT,
    text: "Hints",
    func: (taskSummary) => taskSummary.type === AdminQueueTaskType.HINT,
  },
];
const ADMIN_QUEUE_TYPE_FILTER_FUNCS = Object.fromEntries(
  ADMIN_QUEUE_TYPE_FILTERS.map(({ type, func }) => [type, func])
);

function AdminQueuePage() {
  const adminQueuePageNum = useNavStore((state) => state.adminQueuePageNum);
  const setAdminQueuePageNum = useNavStore(
    (state) => state.setAdminQueuePageNum
  );
  const staffId = useAdminPreferencesStore((state) => state.staffId);
  const setStaffId = useAdminPreferencesStore((state) => state.setStaffId);
  const [staffIdInputVal, setStaffIdInputVal] = useState("");
  const [teamIdFilter, setTeamIdFilter] = useState("");
  const [puzNameFilter, setPuzNameFilter] = useState("");
  const typeFilter = useNavStore((state) => state.adminQueueTypeFilter);
  const setTypeFilter = useNavStore((state) => state.setAdminQueueTypeFilter);
  const teamNameFilter = useNavStore((state) => state.adminQueueTeamNameFilter);
  const setTeamNameFilter = useNavStore(
    (state) => state.setAdminQueueTeamNameFilter
  );
  const isAdminQueuePosthuntView = useNavStore(
    (state) => state.isAdminQueuePosthuntView
  );
  const setIsAdminQueuePosthuntView = useNavStore(
    (state) => state.setIsAdminQueuePosthuntView
  );
  const adminQueueShowPublicPuzzles = useNavStore(
    (state) => state.adminQueueShowPublicPuzzles
  );
  const setAdminQueueShowPublicPuzzles = useNavStore(
    (state) => state.setAdminQueueShowPublicPuzzles
  );
  const numTasksPerPage = 10;

  useRedirectIfNonAdmin();

  const adminQueueScopeState = useGlobalScopeState(ScopeType.ADMIN_QUEUE);
  const huntAdminScopeState = useGlobalScopeState(ScopeType.HUNT_ADMIN);
  const puzzleDisplayNames = usePuzzleDisplayNames();

  const teamTasks: {
    [teamId: string]: {
      teamId: string;
      displayName?: string;
      numTasks: number;
    };
  } = {};
  if (adminQueueScopeState !== null && huntAdminScopeState !== null)
    for (const { teamId } of Object.values(adminQueueScopeState.tasks)) {
      const { displayName } = huntAdminScopeState.teamAdminData[teamId] ?? {};
      (teamTasks[teamId] ??= { teamId, displayName, numTasks: 0 }).numTasks++;
    }
  const teamOptions = Object.values(teamTasks).sort((a, b) => {
    const tasksDiff = b.numTasks - a.numTasks;
    if (tasksDiff !== 0) return tasksDiff;
    return (a.displayName ?? "").localeCompare(b.displayName ?? "");
  });

  const puzzleTasks: {
    [puzName: string]: {
      puzName: string;
      order: number;
      displayName: string;
      numTasks: number;
    };
  } = {};
  if (
    adminQueueScopeState !== null &&
    huntAdminScopeState !== null &&
    puzzleDisplayNames !== null
  )
    for (const taskSummary of Object.values(adminQueueScopeState.tasks)) {
      if (taskSummary.type !== AdminQueueTaskType.HINT) continue;
      const { puzName } = taskSummary;
      const displayName = puzzleDisplayNames[puzName] ?? "Unknown puzzle";
      const order = huntAdminScopeState.puzzles[puzName]?.order ?? 0;
      puzzleTasks[puzName] ??= { puzName, displayName, order, numTasks: 0 };
      puzzleTasks[puzName].numTasks++;
    }
  const puzzleOptions = Object.values(puzzleTasks).sort((a, b) => {
    const tasksDiff = b.numTasks - a.numTasks;
    if (tasksDiff !== 0) return tasksDiff;
    return a.order - b.order;
  });

  const taskFilter = useCallback(
    ([taskId, taskSummary]: [string, AdminQueueTaskSummary]) => {
      const { teamId } = taskSummary;
      return (
        (teamIdFilter === "" || teamIdFilter === teamId) &&
        (puzNameFilter === "" ||
          (taskSummary.type === AdminQueueTaskType.HINT &&
            puzNameFilter === taskSummary.puzName)) &&
        (
          huntAdminScopeState?.teamAdminData[teamId]?.displayName
            .toLowerCase()
            .trim() ?? ""
        ).includes(teamNameFilter.toLowerCase().trim()) &&
        Object.entries(typeFilter).some(
          ([filterType, isEnabled]) =>
            isEnabled && ADMIN_QUEUE_TYPE_FILTER_FUNCS[filterType](taskSummary)
        ) &&
        (!isAdminQueuePosthuntView ||
          (taskSummary.type === AdminQueueTaskType.PUZZLE_SUBMISSION &&
            !(taskSummary.isPrivate ?? false))) &&
        (adminQueueShowPublicPuzzles ||
          (taskSummary.type === AdminQueueTaskType.PUZZLE_SUBMISSION &&
            !(taskSummary.isPublicCustomPuzzle ?? false)))
      );
    },
    [
      teamIdFilter,
      puzNameFilter,
      teamNameFilter,
      huntAdminScopeState,
      typeFilter,
      isAdminQueuePosthuntView,
      adminQueueShowPublicPuzzles,
    ]
  );

  return (
    <div className="centered-page admin-queue-page">
      <Modal
        isOpen={staffId === null}
        wrapperClassName="admin-queue-staff-id-modal-wrapper"
      >
        <div>Enter your Discord username.</div>
        <input
          type="text"
          value={staffIdInputVal}
          onChange={(e) => setStaffIdInputVal(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") setStaffId(staffIdInputVal.trim());
          }}
        />
        <button
          onClick={() => {
            setStaffId(staffIdInputVal.trim());
          }}
          disabled={staffIdInputVal.trim() === ""}
        >
          Let&rsquo;s go!
        </button>
      </Modal>
      <div className="admin-queue-header">
        <div>
          <h1>Admin Task Queue</h1>
        </div>
        <div className="admin-queue-header-actions">
          <div className="admin-queue-team-name-filter">
            <Twemoji tag="span">🔍</Twemoji>{" "}
            <input
              type="text"
              value={teamNameFilter}
              onChange={(e) => {
                setTeamNameFilter(e.target.value);
              }}
            />
          </div>
          {teamOptions.length === 0 ? null : (
            <select
              className="admin-queue-filter-select"
              onChange={(evt) => setTeamIdFilter(evt.target.value)}
            >
              <option value="">👥 Filter by team...</option>
              {teamOptions.map(({ teamId, displayName, numTasks }) => (
                <option key={teamId} value={teamId}>
                  {displayName ?? `<unknown team ${teamId}>`} ({numTasks})
                </option>
              ))}
            </select>
          )}
          {puzzleOptions.length === 0 ? null : (
            <select
              className="admin-queue-filter-select"
              onChange={(evt) => setPuzNameFilter(evt.target.value)}
            >
              <option value="">🧩 Filter hints by puzzle...</option>
              {puzzleOptions.map(({ puzName, displayName, numTasks }) => (
                <option key={puzName} value={puzName}>
                  {displayName} ({numTasks})
                </option>
              ))}
            </select>
          )}
          <div className="admin-queue-header-staff-id">
            <div>You are: {staffId}</div>
            <div>
              <button onClick={() => setStaffId(null)}>Change</button>
            </div>
          </div>
        </div>
      </div>
      <div className="admin-queue-header admin-queue-type-filter">
        {ADMIN_QUEUE_TYPE_FILTERS.map(({ type, text }) => (
          <button
            key={type}
            className={clsObj({ active: typeFilter[type] })}
            onClick={() => {
              setTypeFilter({
                ...typeFilter,
                [type]: !typeFilter[type],
              });
            }}
          >
            {text}
          </button>
        ))}
      </div>
      <div className="admin-queue-header admin-queue-type-filter">
        <button
          className={clsObj({ active: isAdminQueuePosthuntView })}
          onClick={() => {
            setIsAdminQueuePosthuntView(!isAdminQueuePosthuntView);
          }}
        >
          Posthunt view
        </button>
        <button
          className={clsObj({ active: adminQueueShowPublicPuzzles })}
          onClick={() => {
            setAdminQueueShowPublicPuzzles(!adminQueueShowPublicPuzzles);
          }}
        >
          Public Puzzles
        </button>
      </div>
      <AdminQueueTaskListDisplay
        taskFilter={taskFilter}
        numTasksPerPage={numTasksPerPage}
        pageNumStore={[adminQueuePageNum, setAdminQueuePageNum]}
      />
    </div>
  );
}

export function AdminQueueTaskPage() {
  const { taskId } = useParams();

  useRedirectIfNonAdmin();

  if (taskId === undefined) throw new Error("expect taskId to be defined");

  return (
    <div className="centered-page admin-queue-page">
      <AdminQueueTaskDisplay taskId={taskId} />
    </div>
  );
}

export function AdminQueueTaskClaimPage() {
  const { taskId } = useParams();
  const staffId = useAdminPreferencesStore((state) => state.staffId);
  const setRedirectRoute = useNavStore((state) => state.setRedirectRoute);
  const sockClient = useSockClient();
  const adminQueueScopeState = useGlobalScopeState(ScopeType.ADMIN_QUEUE);

  useRedirectIfNonAdmin();

  if (taskId === undefined) throw new Error("expect taskId to be defined");
  const isTaskVisible = adminQueueScopeState?.tasks[taskId] !== undefined;

  useEffect(() => {
    // wait for the task to become visible
    if (!isTaskVisible) return;

    if (staffId === null) {
      toastr.error("Unknown claimer.");
      setRedirectRoute({ route: "admin_queue", replace: true });
      return;
    }
    (async () => {
      const isSuccessful = await sendAdminQueueClaimStepAsync(
        sockClient,
        taskId,
        staffId
      );
      setRedirectRoute(
        isSuccessful
          ? {
              route: "admin_queue_task",
              params: { taskId },
              replace: true,
            }
          : {
              route: "admin_queue",
              replace: true,
            }
      );
    })().catch(console.error);
  }, [staffId, setRedirectRoute, sockClient, taskId, isTaskVisible]);

  return <LargeSpinner />;
}

export default AdminQueuePage;
